import { isTeam } from '../utils/organisationUtils'

const GA_MEASUREMENT_ID = process.env.VUE_APP_GA_MEASUREMENT_ID

/**
 * Google Analytics 4 service for tracking user activity.
 * User-scoped properties (user_id, organisation, team, user_role) are set once on login.
 * Event-scoped properties (accommodation) are sent per event.
 */
export const analyticsService = {
  /**
   * Initialize GA4 with user properties after login.
   * Call this after fetching user profile from /Users/me
   * @param {Object} profile - User profile from API
   */
  initUser(profile) {
    if (!window.gtag || !GA_MEASUREMENT_ID || !profile) return

    // Set built-in user ID
    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: profile.id
    })

    // Derive parent organisation and team from profile.organisation.
    // If the user belongs to a team, organisation.parentOrganisation is the parent org.
    // If the user belongs to a parent org directly, there is no team.
    const org = profile.organisation
    let parentOrgId = null
    let parentOrgName = null
    let teamId = null
    let teamName = null

    if (org) {
      if (isTeam(org)) {
        parentOrgId = org.parentOrganisation?.id ? String(org.parentOrganisation.id) : null
        parentOrgName = org.parentOrganisation?.name || null
        teamId = String(org.id)
        teamName = org.name
      } else {
        parentOrgId = String(org.id)
        parentOrgName = org.name
      }
    }

    // Set user-scoped custom dimensions
    window.gtag('set', 'user_properties', {
      organisation_id: parentOrgId,
      organisation_name: parentOrgName,
      team_id: teamId,
      team_name: teamName
    })
  },

  /**
   * Set user role after roles are loaded from JWT token.
   * @param {string[]} roles - Array of role names (e.g., ['Administrators', 'Users'])
   */
  setUserRole(roles) {
    if (!window.gtag || !GA_MEASUREMENT_ID) return

    // Use the highest privilege role for reporting
    // Priority: Administrators > Managers > Users
    let userRole = null
    if (Array.isArray(roles) && roles.length > 0) {
      if (roles.includes('Administrators')) {
        userRole = 'admin'
      } else if (roles.includes('Managers')) {
        userRole = 'manager'
      } else if (roles.includes('Users')) {
        userRole = 'user'
      }
    }

    window.gtag('set', 'user_properties', {
      user_role: userRole
    })
  },

  /**
   * Clear user properties on logout.
   */
  clearUser() {
    if (!window.gtag || !GA_MEASUREMENT_ID) return

    window.gtag('set', 'user_properties', {
      organisation_id: null,
      organisation_name: null,
      team_id: null,
      team_name: null,
      user_role: null
    })

    // Clear built-in user_id so subsequent events are not attributed to the previous user
    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: null
    })
  },

  /**
   * Track accommodation view event.
   * @param {Object} accommodation - Accommodation object with id and name
   */
  trackAccommodationView(accommodation) {
    if (!window.gtag || !GA_MEASUREMENT_ID || !accommodation) return

    window.gtag('event', 'view_accommodation', {
      accommodation_id: String(accommodation.id),
      accommodation_name: accommodation.name
    })
  }
}
